if (!customElements.get('td-reviews')) {
  /**
   * Chip switcher for the Loox Reviews Widget.
   *
   * Loox renders one widget per product ID into a cross-origin iframe, so switching product
   * means rewriting the product-ID segment of the src Loox itself generated. Everything
   * inside the frame — rating header, distribution bars, AI summary, reviews — swaps with
   * it, and Loox's own postMessage resize handler follows the new document without any
   * re-initialisation on our side.
   *
   * The URL is never rebuilt from scratch: it carries params Loox owns — `h` is Loox's own
   * `loox_global_hash`, verified identical on a live page — so only the one path segment is
   * replaced and anything unexpected leaves the widget alone.
   *
   * The frame belongs to a third party. It may never appear (ad blocker, consent gate, CSP)
   * and once it does it may never fire `load`. Both have to leave a usable page, so chips
   * stay disabled until a frame is confirmed, and every piece of transient state has a
   * timeout that clears it.
   */
  const FRAME_WAIT_MS = 8000;
  const LOAD_TIMEOUT_MS = 8000;
  const LOOX_ORIGIN = 'https://loox.io';

  class TdReviews extends HTMLElement {
    connectedCallback() {
      // Re-entrant safe: an element moved in the DOM connects again, and the previous
      // controller still owns a listener on `document`.
      this.abort?.abort();
      this.clearPendingTimers();
      this.abort = new AbortController();
      this.chips = Array.from(this.querySelectorAll('.td-rev__chip'));
      if (this.chips.length === 0) return;

      this.status = this.querySelector('.td-rev__status');
      this.pendingTimers = new Set();
      this.statusTemplate = this.dataset.tdStatusTemplate || '';
      this.swapSeq = 0;
      const { signal } = this.abort;

      // Chips do nothing until there is a frame to drive. A chip that highlights, rewrites
      // the URL and announces a product while the widget below is unchanged is worse than
      // one that is visibly not ready yet.
      this.setChipsEnabled(false);

      this.chips.forEach((chip, i) => {
        chip.addEventListener('click', () => this.activate(i), { signal });
        chip.addEventListener('keydown', (event) => this.onKeydown(event, i), { signal });
      });

      // Paint the deep-linked chip immediately; the widget swap waits for the frame.
      this.restoreFromUrl();

      this.whenFrameReady().then((frame) => {
        if (!this.isConnected) return;
        if (!frame) {
          this.onFrameUnavailable();
          return;
        }
        this.frame = frame;
        this.setChipsEnabled(true);
        this.syncToActiveChip();
      });

      document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
    }

    clearPendingTimers() {
      this.pendingTimers?.forEach((id) => clearTimeout(id));
      this.pendingTimers?.clear();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.stopWaiting?.();
      this.settleSwap?.();
      this.clearPendingTimers();
      this.frame = null;
      delete this.dataset.tdLoading;
      delete this.dataset.tdUnavailable;
    }

    onBlockSelect = (event) => {
      const index = this.chips.indexOf(event.target.closest?.('.td-rev__chip'));
      // An editor selection is not a shopper choosing a product: no URL write, no
      // live-region announcement.
      if (index > -1) this.activate(index, false, false);
    };

    setChipsEnabled(enabled) {
      this.chips.forEach((chip) => {
        chip.disabled = !enabled;
      });
    }

    /**
     * Loox never arrived. Hide the switcher rather than leave dead controls on the page:
     * a single-product widget with no chips is a coherent interface, a chip row that does
     * nothing is not.
     */
    onFrameUnavailable() {
      this.dataset.tdUnavailable = 'true';
      if (!window.Shopify?.designMode) return;
      const host = this.querySelector('.td-rev__widget');
      const note = this.dataset.tdEditorNote;
      if (!host || !note || host.querySelector('.td-rev__editor-note')) return;
      const el = document.createElement('p');
      el.className = 'td-rev__editor-note';
      el.textContent = note;
      host.appendChild(el);
    }

    /**
     * Loox's script is async and fires no ready event. Observe rather than poll, and filter
     * on `src`: we need to wake when Loox *sets* the src, not when it creates the empty
     * node — resolving on a src-less iframe would hand `swap()` an unparseable URL.
     */
    whenFrameReady() {
      return new Promise((resolve) => {
        const find = () =>
          Array.from(this.querySelectorAll('iframe')).find((el) => {
            try {
              return new URL(el.src).origin === LOOX_ORIGIN;
            } catch {
              return false;
            }
          });

        const existing = find();
        if (existing) {
          resolve(existing);
          return;
        }

        const host = this.querySelector('.td-rev__widget') || this;
        const stop = () => {
          observer.disconnect();
          clearTimeout(timer);
          this.stopWaiting = null;
        };
        const observer = new MutationObserver(() => {
          const frame = find();
          if (frame) {
            stop();
            resolve(frame);
          }
        });
        const timer = setTimeout(() => {
          stop();
          resolve(null);
        }, FRAME_WAIT_MS);

        this.stopWaiting = () => {
          stop();
          resolve(null);
        };
        observer.observe(host, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['src'],
        });
      });
    }

    /**
     * The section emits the first tab's product ID server-side, so the frame normally
     * starts on the right product. The exception is a `?reviews=<handle>` deep link:
     * restoreFromUrl paints that chip before the frame exists, and this reconciles the
     * widget to it once the frame appears.
     */
    syncToActiveChip() {
      const chip = this.chips.find((c) => c.getAttribute('aria-checked') === 'true') || this.chips[0];
      const wanted = chip?.dataset.tdProductId;
      if (!wanted || this.currentProductId() === wanted) return;
      this.swap(wanted);
    }

    currentProductId() {
      try {
        const match = new URL(this.frame.src).pathname.match(/\/reviews\/(\d+)$/);
        return match && match[1];
      } catch {
        return null;
      }
    }

    /**
     * `activate` writes ?reviews=<handle>; without the read side the param was written and
     * never used, so a shared or reloaded link always landed back on the first chip.
     */
    restoreFromUrl() {
      const wanted = new URL(window.location.href).searchParams.get('reviews');
      if (!wanted) return;
      const index = this.chips.findIndex((c) => c.dataset.tdProductHandle === wanted);
      if (index > -1) this.activate(index, false, false);
    }

    activate(index, focus = true, fromUser = true) {
      // Re-selecting the active chip is a no-op: swap() would bail on an identical
      // pathname anyway, but announcing and rewriting the URL for it is noise.
      if (this.chips[index]?.getAttribute('aria-checked') === 'true' && this.frame) {
        if (focus) this.chips[index].focus();
        return;
      }

      this.chips.forEach((chip, i) => {
        const selected = i === index;
        chip.setAttribute('aria-checked', selected ? 'true' : 'false');
        chip.tabIndex = selected ? 0 : -1;
      });

      const chip = this.chips[index];
      if (focus) chip.focus();
      this.swap(chip.dataset.tdProductId);

      // Only announce a real choice. Announcing during connectedCallback would speak over
      // anyone arriving on a shared ?reviews= link.
      if (fromUser && this.status && this.statusTemplate) {
        this.status.textContent = this.statusTemplate.replace('[product]', chip.dataset.tdLabel || '');
      }

      // replaceState, not pushState: Back should leave the page rather than walk the
      // shopper back through every chip they tried.
      if (fromUser && chip.dataset.tdProductHandle && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('reviews', chip.dataset.tdProductHandle);
        window.history.replaceState({}, '', url);
      }
    }

    swap(productId) {
      if (!this.frame || !productId) return;
      // Loox can re-render its own container; our reference would then be detached and
      // setting src on it would silently do nothing, forever.
      if (!this.contains(this.frame)) return;

      let url;
      try {
        url = new URL(this.frame.src);
      } catch {
        return;
      }

      const next = url.pathname.replace(/\/reviews\/\d+$/, () => `/reviews/${productId}`);
      // The regex missing means Loox changed its URL shape. Leave the widget working.
      if (next === url.pathname) return;
      url.pathname = next;

      this.dataset.tdLoading = 'true';

      const token = ++this.swapSeq;
      const settle = () => {
        if (token !== this.swapSeq) return; // a later swap owns the state now
        clearTimeout(bail);
        this.pendingTimers.delete(bail);
        this.settleSwap = null;
        delete this.dataset.tdLoading;
      };
      // The backstop. `load` is the only other thing that clears this, and it belongs to a
      // cross-origin document that may never fire it.
      const bail = setTimeout(settle, LOAD_TIMEOUT_MS);
      this.pendingTimers.add(bail);
      this.settleSwap = settle;

      // Replace the element rather than assigning `src`. Navigating an existing iframe
      // pushes onto the joint session history, so Back would step through every chip the
      // shopper tried instead of leaving the page — measured: `src` assignment adds a
      // history entry, replacing the element adds none. Loox resolves the frame by id, so
      // its postMessage resize handler picks the new element up either way (verified).
      //
      // The clone carries the outgoing frame's inline height, which holds the page steady
      // while the new document loads instead of collapsing to zero under a scrolled
      // shopper — and if Loox never resizes, it keeps that height rather than vanishing.
      const fresh = this.frame.cloneNode(false);
      fresh.src = url.toString();
      fresh.addEventListener('load', settle, { once: true, signal: this.abort.signal });
      this.frame.replaceWith(fresh);
      this.frame = fresh;
    }

    onKeydown(event, index) {
      const last = this.chips.length - 1;
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      if (next !== null) {
        event.preventDefault();
        this.activate(next);
      }
    }
  }

  customElements.define('td-reviews', TdReviews);
}
